import { FC } from 'react';
import { useQuery } from 'react-query';
import { getScopedPolicyWithParents, patchPolicy } from '@/lib/services/policy.service';
import useToast from '@/hooks/use-toast.hook';
import { PolicyModel } from '@/lib/models/policy-model/policy.model';
import useFormHandle from '@/hooks/use-form-handle.hook';
import FormButtons from '@/components/form/form-buttons';
import { QueryKey } from '@/lib/query-client';
import { Form } from '@/components/form';
import useFeature from '@/hooks/use-feature.hook';
import PageFeatureToast from '@/components/page-feature-toast';
import { FieldError } from '@/lib/services';
import ClassifierControls from '@/components/form/classifier-controls';
import { Stack } from '@mui/material';
import { createUseStyles } from 'react-jss';

const fields = ['intents', 'classifierControls', 'regexPatterns'];
const formModel = new PolicyModel();

const useStyles = createUseStyles({
  classificationForm: {
    height: '100%',
    minHeight: 'calc(100vh - 120px)', // Put in place to have the sticky footer work when too little content. Account for header (~64px) + tabs (~56px)
    display: 'flex',
    flexDirection: 'column',
  },
  contentWrapper: {
    flexGrow: 1,
    flexDirection: 'column',
  },
});

interface Props {
  policyId: string;
}

const MetadataForm: FC<Props> = ({ policyId }) => {
  const { toast, errorToast } = useToast();
  const { canChangePolicy } = useFeature();
  const styles = useStyles();

  const formHandle = useFormHandle({
    initialValues: formModel,
    validationSchema: formModel.schemaSlice(fields),
    onSubmit: async (values, { setSubmitting, resetForm, setFieldError }) => {
      const setFieldErrors = (fieldErrors: FieldError[]) => {
        fieldErrors.forEach(({ msg, path }) => setFieldError(path, msg));
      };

      const { patched, fieldErrors, error } = await patchPolicy(
        values.id,
        values.propSlice(fields)
      );
      setSubmitting(false);

      if (patched) {
        resetForm({ values });
        toast('The policy was updated');
        return;
      }

      if (fieldErrors) {
        setFieldErrors(fieldErrors);
        return;
      }

      errorToast(error || 'The policy was not updated');
    },
  });

  const query = useQuery([QueryKey.ScopedPolicyWithParents, policyId], async () =>
    getScopedPolicyWithParents(policyId)
  );
  const { data } = query;
  const [policy, defaultPolicy] = data || [null, new PolicyModel()];
  const { isDefault: isDefaultPolicy } = policy || { isDefault: false };

  return (
    <Form formHandle={formHandle} query={query} className={styles.classificationForm}>
      <PageFeatureToast featureId="change-policy" can={canChangePolicy} />
      <Stack className={styles.contentWrapper}>
        <ClassifierControls
          formHandle={formHandle}
          readonly={!canChangePolicy}
          defaultPolicy={isDefaultPolicy ? undefined : defaultPolicy}
          withNotSet={!isDefaultPolicy}
        />
      </Stack>

      <FormButtons formHandle={formHandle} readonly={!canChangePolicy} />
    </Form>
  );
};

export default MetadataForm;
